import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiEye, FiSend, FiTrash2, FiEdit2, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, Button, Badge, Modal, ConfirmDialog, Spinner } from '../../components/common';
import CmsField from '../cms/components/CmsField';
import ClientSearchAutocomplete from './components/ClientSearchAutocomplete';
import ClientFormModal from './components/ClientFormModal';
import ProformaLineItemsTable, { newProformaLine } from './components/ProformaLineItemsTable';
import ProformaTotalsPanel, { trimNumber } from './components/ProformaTotalsPanel';
import InvoicePdfFrame from './components/InvoicePdfFrame';
import { lineKey } from './components/InvoiceLineItemsTable';
import { proformaInvoiceService, PI_HEADER_FIELDS } from '../../services/proformaInvoiceService';
import { billingClientService } from '../../services/billingClientService';
import { billingSettingsService } from '../../services/billingSettingsService';
import { getApiError, getApiFieldErrors } from '../../services/cmsService';
import { formatINR, splitRowErrors, todayISO } from './billingUtils';
import './Billing.css';
import './InvoiceEditorPage.css';

const DEFAULTS = { hsn: '998391', gstRate: '18', advancePercent: '40', unit: 'MW' };
const NO_ERRORS = { fields: {}, rows: {} };

/** Printed header boxes, in PDF order. Dates use a date picker. */
const HEADER_INPUTS = [
  { key: 'paymentTerms', label: 'Mode/Terms of payment', placeholder: 'e.g. Bank transfer' },
  { key: 'deliveryTerms', label: 'Terms of delivery' },
  { key: 'referenceNo', label: 'Reference No. & date' },
  { key: 'otherReferences', label: 'Other references' },
  { key: 'buyerOrderNo', label: 'Buyer’s order No.' },
  { key: 'buyerOrderDate', label: 'Buyer’s order date', type: 'date' },
  { key: 'deliveryNote', label: 'Delivery note' },
  { key: 'deliveryNoteDate', label: 'Delivery note date', type: 'date' },
  { key: 'dispatchDocNo', label: 'Dispatch doc No.' },
  { key: 'dispatchedThrough', label: 'Dispatched through' },
  { key: 'destination', label: 'Destination' },
];

const API_KEY = PI_HEADER_FIELDS;
const EMPTY_HEADERS = Object.fromEntries(Object.keys(PI_HEADER_FIELDS).map((key) => [key, '']));

const emptyForm = (d = DEFAULTS) => ({
  piDate: todayISO(),
  unit: d.unit,
  gstRate: '',
  advancePercent: d.advancePercent,
  notes: '',
  ...EMPTY_HEADERS,
});

/** "2.400" → "2.4", "0.00" → "" — easier to edit than the stored decimals. */
const lineFromItem = (item, key) => ({
  key: key ?? lineKey(),
  description: item.description,
  hsnCode: item.hsnCode,
  quantity: trimNumber(item.quantity),
  rate: item.rate,
  discountPercent: Number(item.discountPercent) ? trimNumber(item.discountPercent) : '',
});

/** What "unsaved" is measured against. Local row keys are not content. */
const snapshotOf = (clientId, form, items) =>
  JSON.stringify({ clientId: clientId ?? null, form, items: items.map(({ key, ...rest }) => rest) });

const isAbort = (error) => error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';

/** A client the API can no longer return (deleted) — rebuilt from the PI snapshot. */
const clientFromProforma = (pi) => ({
  id: pi.clientId,
  name: pi.clientName,
  gstin: pi.clientGstin,
  address: pi.clientAddress,
  clientCode: pi.clientCode,
  stateCode: pi.clientStateCode,
  stateName: pi.clientStateName,
  unavailable: true,
});

const loadDefaults = async () => {
  try {
    const { values } = await billingSettingsService.get();
    return {
      hsn: values.default_hsn_code || DEFAULTS.hsn,
      gstRate: String(values.default_gst_rate ?? DEFAULTS.gstRate),
      // Blank in settings = new PIs ask for no advance.
      advancePercent: String(values.pi_default_advance_percent ?? ''),
      unit: values.pi_default_unit || DEFAULTS.unit,
    };
  } catch {
    return DEFAULTS; // the built-in defaults match the seeded settings
  }
};

const ProformaInvoiceEditorPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get('client_id');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState(DEFAULTS);
  const [proforma, setProforma] = useState(null);
  const [client, setClient] = useState(null);
  const [changingClient, setChangingClient] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState(() => snapshotOf(null, emptyForm(), []));
  const [errors, setErrors] = useState(NO_ERRORS);

  const [totals, setTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(false);
  const [totalsError, setTotalsError] = useState('');

  const [busy, setBusy] = useState(null); // 'save' | 'preview' | 'issue' | 'delete' | 'client'
  const [clientModal, setClientModal] = useState(null); // { mode: 'create', name } | { mode: 'edit' }
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Set when a first save moves /new → /:id/edit, so that route change
  // doesn't reload what is already on screen.
  const skipLoadRef = useRef(null);
  const defaultsRef = useRef(null);

  const snapshot = snapshotOf(client?.id, form, items);
  const isDirty = snapshot !== savedSnapshot;

  /* ---------------------------------------------------------------- *
   * Loading
   * ---------------------------------------------------------------- */

  useEffect(() => {
    if (isEdit && skipLoadRef.current === id) {
      skipLoadRef.current = null;
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        defaultsRef.current = defaultsRef.current ?? (await loadDefaults());
        const d = defaultsRef.current;
        if (cancelled) return;
        setDefaults(d);
        setProforma(null);
        setClient(null);
        setChangingClient(false);
        setErrors(NO_ERRORS);
        setTotals(null);

        if (isEdit) {
          const pi = await proformaInvoiceService.getById(id);
          if (cancelled) return;
          if (!pi.permissions.canEdit) {
            toast.info('Issued proforma invoices are locked. Duplicate it to make a revised one.');
            navigate(`/billing/proforma-invoices/${id}`, { replace: true });
            return;
          }
          const full = await billingClientService.getById(pi.clientId).catch(() => clientFromProforma(pi));
          if (cancelled) return;

          const nextForm = {
            piDate: pi.piDate,
            unit: pi.unit,
            // Only show an override when it differs from the default rate.
            gstRate: Number(pi.gstRate) !== Number(d.gstRate) ? trimNumber(pi.gstRate) : '',
            advancePercent: pi.advancePercent == null ? '' : trimNumber(pi.advancePercent),
            notes: pi.notes,
            ...Object.fromEntries(Object.keys(PI_HEADER_FIELDS).map((key) => [key, pi[key] ?? ''])),
          };
          const nextItems = pi.items.map((item) => lineFromItem(item));
          setProforma(pi);
          setClient(full);
          setForm(nextForm);
          setItems(nextItems);
          setSavedSnapshot(snapshotOf(full.id, nextForm, nextItems));
          setTotals({ ...pi, halfRate: null, lines: pi.items, stateMissing: !pi.taxType });
        } else {
          const fresh = emptyForm(d);
          const firstLine = [newProformaLine(d.hsn)];
          setForm(fresh);
          setItems(firstLine);
          setSavedSnapshot(snapshotOf(null, fresh, firstLine));
          if (clientIdParam) {
            const full = await billingClientService.getById(clientIdParam);
            if (cancelled) return;
            setClient(full);
          }
        }
      } catch (error) {
        if (cancelled) return;
        toast.error(getApiError(error, isEdit ? 'Failed to load the proforma invoice' : 'Failed to load the client'));
        if (isEdit) navigate('/billing/proforma-invoices', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, clientIdParam]);

  /* ---------------------------------------------------------------- *
   * Live totals — the server does all of the math
   * ---------------------------------------------------------------- */

  const mathKey = items.map((l) => `${l.quantity}\u0002${l.rate}\u0002${l.discountPercent}`).join('\u0001') + `#${items.length}`;

  useEffect(() => {
    if (!client) {
      setTotals(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setTotalsLoading(true);
      try {
        const next = await proformaInvoiceService.calculate(
          { clientId: client.id, gstRate: form.gstRate, advancePercent: form.advancePercent, items },
          { signal: controller.signal }
        );
        setTotals(next);
        setTotalsError('');
      } catch (error) {
        if (isAbort(error)) return;
        setTotalsError(getApiError(error, 'Could not calculate the totals'));
      } finally {
        if (!controller.signal.aborted) setTotalsLoading(false);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // `items` is read through mathKey so typing a description doesn't recalculate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?.id, client?.stateCode, form.gstRate, form.advancePercent, mathKey]);

  /* ---------------------------------------------------------------- *
   * Unsaved-changes guard
   * ---------------------------------------------------------------- */

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // BrowserRouter has no useBlocker, so the editor's own exits ask first.
  const leave = (to) => {
    if (isDirty) setLeaveTarget(to);
    else navigate(to);
  };

  /* ---------------------------------------------------------------- *
   * Client selection
   * ---------------------------------------------------------------- */

  const handleSelectClient = async (row) => {
    if (client && row.id === client.id) {
      setChangingClient(false);
      return;
    }
    setBusy('client');
    try {
      setClient(await billingClientService.getById(row.id));
      setChangingClient(false);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load the client'));
    } finally {
      setBusy(null);
    }
  };

  const handleClientSaved = async (saved) => {
    if (clientModal?.mode === 'edit') {
      // Refresh details (and the state, which decides the tax type).
      try {
        setClient(await billingClientService.getById(saved.id));
      } catch {
        setClient((prev) => ({ ...prev, ...saved }));
      }
      return;
    }
    handleSelectClient(saved);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    const apiKey = { piDate: 'pi_date', gstRate: 'gst_rate', advancePercent: 'advance_percent' }[field] ?? API_KEY[field] ?? field;
    setErrors((prev) => ({ ...prev, fields: { ...prev.fields, [apiKey]: undefined } }));
  };

  /* ---------------------------------------------------------------- *
   * Save / preview / issue / delete
   * ---------------------------------------------------------------- */

  const save = async ({ quiet = false } = {}) => {
    if (!client) {
      toast.error('Select a client first');
      return null;
    }
    setErrors(NO_ERRORS);
    const payload = { clientId: client.id, ...form, items };

    try {
      const saved = proforma
        ? await proformaInvoiceService.updateDraft(proforma.id, payload)
        : await proformaInvoiceService.createDraft(payload);

      // Adopt the server's normalised values (blank HSN → default, "2.40" → "2.4").
      const nextItems = saved.items.map((item, index) => lineFromItem(item, items[index]?.key));
      const nextForm = { ...form, unit: saved.unit };
      setProforma(saved);
      setItems(nextItems);
      setForm(nextForm);
      setSavedSnapshot(snapshotOf(client.id, nextForm, nextItems));

      if (!proforma) {
        skipLoadRef.current = String(saved.id);
        navigate(`/billing/proforma-invoices/${saved.id}/edit`, { replace: true });
      }
      if (!quiet) toast.success('Draft saved');
      return saved;
    } catch (error) {
      setErrors(splitRowErrors(getApiFieldErrors(error)));
      toast.error(getApiError(error, 'Failed to save the draft'));
      return null;
    }
  };

  const handleSave = async () => {
    setBusy('save');
    await save();
    setBusy(null);
  };

  const handlePreview = async () => {
    setBusy('preview');
    const target = isDirty || !proforma ? await save({ quiet: true }) : proforma;
    setBusy(null);
    if (!target) return;
    setPreviewVersion((v) => v + 1);
    setPreviewOpen(true);
  };

  const handleIssueClick = async () => {
    if (isDirty || !proforma) {
      setBusy('issue');
      const saved = await save({ quiet: true });
      setBusy(null);
      if (!saved) return;
    }
    setConfirmIssue(true);
  };

  const doIssue = async () => {
    setBusy('issue');
    try {
      const issued = await proformaInvoiceService.issue(proforma.id);
      toast.success(`Proforma invoice ${issued.number} issued`);
      setSavedSnapshot(snapshot);
      navigate(`/billing/proforma-invoices/${issued.id}`);
    } catch (error) {
      const fields = getApiFieldErrors(error);
      setErrors((prev) => ({ ...prev, fields: { ...prev.fields, ...fields } }));
      toast.error(getApiError(error, 'Failed to issue the proforma invoice'));
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    setBusy('delete');
    try {
      await proformaInvoiceService.delete(proforma.id);
      toast.success('Draft deleted');
      setSavedSnapshot(snapshot);
      navigate('/billing/proforma-invoices');
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete the draft'));
    } finally {
      setBusy(null);
    }
  };

  /* ---------------------------------------------------------------- */

  const stateMissing = Boolean(client) && (totals ? totals.stateMissing : !client.stateCode);
  const formErrors = errors.fields;
  const topLevelErrors = [formErrors.proforma_invoice, formErrors.items, formErrors.billing_client_id].filter(Boolean);
  const headerHasValues = HEADER_INPUTS.some(({ key }) => form[key]) || HEADER_INPUTS.some(({ key }) => formErrors[API_KEY[key]]);
  const advanceHint = form.advancePercent === ''
    ? 'Blank = no advance requested.'
    : `Printed as “${trimNumber(form.advancePercent)}% ADVANCE” unless Terms of delivery is set.`;

  if (loading) {
    return (
      <div className="bl-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="bl-page invoice-editor">
      <button
        type="button"
        className="bl-back"
        onClick={() => leave(proforma ? `/billing/proforma-invoices/${proforma.id}` : '/billing/proforma-invoices')}
      >
        <FiArrowLeft /> {proforma ? 'Back to proforma invoice' : 'Back to proforma invoices'}
      </button>

      <div className="ie-header">
        <div className="ie-title">
          <h1>{proforma ? 'Edit draft proforma invoice' : 'New proforma invoice'}</h1>
          <div className="ie-title-meta">
            <Badge variant="default" size="small">Draft</Badge>
            {isDirty ? (
              <span className="ie-dirty">Unsaved changes</span>
            ) : (
              proforma && <span className="bl-muted">All changes saved</span>
            )}
          </div>
        </div>
        <div className="bl-header-actions">
          {proforma && (
            <Button variant="ghost" icon={<FiTrash2 />} onClick={() => setConfirmDelete(true)} disabled={Boolean(busy)}>
              Delete
            </Button>
          )}
          <Button variant="outline" icon={<FiEye />} onClick={handlePreview} loading={busy === 'preview'} disabled={!client || Boolean(busy)}>
            Preview PDF
          </Button>
          <Button variant="secondary" icon={<FiSave />} onClick={handleSave} loading={busy === 'save'} disabled={!client || Boolean(busy)}>
            Save draft
          </Button>
          <Button
            icon={<FiSend />}
            onClick={handleIssueClick}
            loading={busy === 'issue'}
            disabled={!client || !items.length || stateMissing || Boolean(busy)}
            title={stateMissing ? 'Add a GSTIN or state to the client first' : undefined}
          >
            Issue PI
          </Button>
        </div>
      </div>

      {topLevelErrors.length > 0 && (
        <div className="bl-banner danger" role="alert">
          <FiAlertTriangle />
          <div className="bl-banner-body">{topLevelErrors.map((msg) => <div key={msg}>{msg}</div>)}</div>
        </div>
      )}

      <div className="ie-layout">
        <div className="ie-main">
          <Card title="Buyer (bill to)" padding="medium">
            {(!client || changingClient) ? (
              <div className="bl-form-stack">
                <ClientSearchAutocomplete
                  autoFocus
                  onSelect={handleSelectClient}
                  onCreateNew={(name) => setClientModal({ mode: 'create', name })}
                />
                {busy === 'client' && <span className="bl-muted">Loading client…</span>}
                {changingClient && (
                  <div>
                    <Button variant="ghost" size="small" onClick={() => setChangingClient(false)}>
                      Keep {client.name}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="ie-client">
                <dl className="bl-info-grid">
                  <div className="bl-span-all">
                    <dt>Bill to</dt>
                    <dd className="ie-client-name">{client.name}</dd>
                  </div>
                  <div>
                    <dt>GSTIN/UIN</dt>
                    <dd className="bl-mono">{client.gstin || '—'}</dd>
                  </div>
                  <div>
                    <dt>Client ID</dt>
                    <dd className="bl-mono">{client.clientCode || '—'}</dd>
                  </div>
                  <div>
                    <dt>State</dt>
                    <dd>{client.stateCode ? `${client.stateCode}${client.stateName ? ` — ${client.stateName}` : ''}` : '—'}</dd>
                  </div>
                  <div className="bl-span-all">
                    <dt>Address</dt>
                    <dd>{client.address || '—'}</dd>
                  </div>
                </dl>

                <div className="ie-client-foot">
                  {totals?.taxLabel && !stateMissing && <Badge variant="primary">{totals.taxLabel}</Badge>}
                  <div className="ie-client-actions">
                    {!client.unavailable && (
                      <Button variant="ghost" size="small" icon={<FiEdit2 />} onClick={() => setClientModal({ mode: 'edit' })}>
                        Edit client
                      </Button>
                    )}
                    <Button variant="outline" size="small" onClick={() => setChangingClient(true)}>
                      Change client
                    </Button>
                  </div>
                </div>

                {client.unavailable && (
                  <div className="bl-banner warning">
                    <FiAlertTriangle />
                    <div className="bl-banner-body">This client has been deleted. The details above are from the draft.</div>
                  </div>
                )}

                {stateMissing && (
                  <div className="bl-banner warning">
                    <FiAlertTriangle />
                    <div className="bl-banner-body">
                      Client state unknown — add a GSTIN or state before issuing.{' '}
                      {!client.unavailable && (
                        <button type="button" className="bl-link" onClick={() => setClientModal({ mode: 'edit' })}>
                          Edit client
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          <Card title="Line items" subtitle="Amount = quantity × rate, less discount. Order = SR No. on the PI." padding="medium">
            <ProformaLineItemsTable
              items={items}
              onChange={setItems}
              unit={form.unit}
              lineAmounts={client ? totals?.lines ?? [] : []}
              rowErrors={errors.rows}
              defaultHsn={defaults.hsn}
            />
          </Card>

          <Card title="Reference details" subtitle="Optional boxes printed in the PI header." padding="medium">
            <details className="ie-advanced" open={headerHasValues}>
              <summary>Payment terms, order and dispatch references</summary>
              <div className="ie-advanced-body">
                <div className="bl-form-grid">
                  {HEADER_INPUTS.map(({ key, label, type, placeholder }) => (
                    <CmsField
                      key={key}
                      id={`pe-${key}`}
                      type={type || 'text'}
                      label={label}
                      placeholder={key === 'deliveryTerms' && form.advancePercent !== '' ? `${trimNumber(form.advancePercent)}% ADVANCE` : placeholder}
                      value={form[key]}
                      onChange={(v) => setField(key, v)}
                      error={formErrors[API_KEY[key]]}
                    />
                  ))}
                </div>
              </div>
            </details>
          </Card>

          <Card title="Internal notes" padding="medium">
            <CmsField
              id="pe-notes"
              type="textarea"
              rows={3}
              placeholder="Visible to admins only; not printed on the PI."
              value={form.notes}
              onChange={(v) => setField('notes', v)}
              error={formErrors.notes}
            />
          </Card>
        </div>

        <aside className="ie-side">
          <Card title="PI details" padding="medium">
            <div className="bl-form-stack">
              <div className="ie-number">
                <span className="cf-label">PI No.</span>
                <span className="ie-number-value">{proforma?.number || 'Assigned on issue'}</span>
              </div>
              <CmsField
                id="pe-date"
                type="date"
                label="PI date"
                required
                value={form.piDate}
                onChange={(v) => setField('piDate', v)}
                error={formErrors.pi_date}
                hint="Decides the financial year of the number."
              />
              <CmsField
                id="pe-advance"
                type="number"
                label="Advance (%)"
                placeholder="No advance"
                value={form.advancePercent}
                onChange={(v) => setField('advancePercent', v)}
                error={formErrors.advance_percent}
                hint={advanceHint}
                inputProps={{ min: 0, max: 100, step: '0.01' }}
              />
              <CmsField
                id="pe-unit"
                label="Quantity unit"
                placeholder={defaults.unit}
                value={form.unit}
                onChange={(v) => setField('unit', v)}
                error={formErrors.unit}
                hint="Printed as “Rate Per MW” and after each quantity."
                inputProps={{ maxLength: 20 }}
              />
              <details className="ie-advanced" open={Boolean(form.gstRate) || Boolean(formErrors.gst_rate)}>
                <summary>Advanced</summary>
                <div className="ie-advanced-body">
                  <CmsField
                    id="pe-gst"
                    type="number"
                    label="GST rate override (%)"
                    placeholder={`Default ${defaults.gstRate}%`}
                    value={form.gstRate}
                    onChange={(v) => setField('gstRate', v)}
                    error={formErrors.gst_rate}
                    hint="Leave blank to use the default rate from Billing Settings."
                    inputProps={{ min: 0, max: 100, step: '0.01' }}
                  />
                </div>
              </details>
            </div>
          </Card>

          <Card title="Totals" padding="medium" className="ie-totals-card">
            <ProformaTotalsPanel totals={client ? totals : null} unit={form.unit} loading={totalsLoading} error={totalsError} />
          </Card>
        </aside>
      </div>

      <ClientFormModal
        isOpen={Boolean(clientModal)}
        onClose={() => setClientModal(null)}
        quick={clientModal?.mode === 'create'}
        initialName={clientModal?.name ?? ''}
        client={clientModal?.mode === 'edit' ? client : null}
        onSaved={handleClientSaved}
      />

      <ConfirmDialog
        isOpen={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={doIssue}
        title="Issue this proforma invoice?"
        message={`This assigns the next PI number and locks it${totals?.totalAmount ? ` for ${formatINR(totals.totalAmount)}` : ''}. To revise it later, duplicate it as a new draft.`}
        confirmText="Issue PI"
        variant="primary"
        loading={busy === 'issue'}
      />

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={doDelete}
        title="Delete draft"
        message="Delete this draft proforma invoice? This cannot be undone."
        confirmText="Delete draft"
        loading={busy === 'delete'}
      />

      <ConfirmDialog
        isOpen={Boolean(leaveTarget)}
        onClose={() => setLeaveTarget(null)}
        onConfirm={() => {
          const to = leaveTarget;
          setSavedSnapshot(snapshot);
          navigate(to);
        }}
        title="Discard unsaved changes?"
        message="You have changes that haven't been saved. Leave without saving?"
        confirmText="Discard changes"
      />

      <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title="Proforma invoice preview" size="xlarge">
        {previewOpen && proforma && <InvoicePdfFrame kind="proforma" invoiceId={proforma.id} version={previewVersion} />}
      </Modal>
    </div>
  );
};

export default ProformaInvoiceEditorPage;
